import { TrajectoryMetadataProvider } from '@trimble/web3d-providers';
import { Web3DViewer } from '@technology/web3d';
import { SetStoreFunction } from 'solid-js/store';
import { useConnectProject } from '@trimble/connect-project-providers';
import { useApiServices } from '../ApiServiceProvider';
import { ProjectDataState, TrajectoryItem } from './DataStateModels';
import { CrsIssueKind } from '@trimble/project-data-panel-types';
import { resolveTrajectorySplittable } from './resolve-trajectory-splittable';
import { useProjectSettings } from '../ProjectSettingsProvider';
import {
  DataLoadingService as BaseDataLoadingService,
  DataLoadingServiceConfig,
  ProviderHandler,
} from '@trimble/data-loading';
import {
  createModelConfig,
  createPointCloudConfig,
  createStyledTrajectoryConfig,
  createTrimBimConfig,
} from './ProviderConfigurations';
import { Point2D } from '@trimble/utilities';
import {
  createPointCloudLoadErrorNotifier,
  PointCloudLoadErrorNotifier,
} from './point-cloud-load-error-notifier';

const POINT_CLOUD_LOAD_CONCURRENCY = 10;

/** Item types supported by the mobile-mapping service */
export type ItemType =
  | 'pointCloud'
  | 'trajectory'
  | 'styled-trajectory'
  | 'model';

export class DataLoadingService extends BaseDataLoadingService<
  ItemType,
  ProjectDataState
> {
  private readonly cancelPointCloudQueries: (
    pointCloudId?: string
  ) => Promise<void>;

  private readonly pointCloudLoadErrorNotifier: PointCloudLoadErrorNotifier;

  private readonly abortAllMismatchChecks: () => void;

  constructor(
    projectDataState: ProjectDataState,
    setProjectDataState: SetStoreFunction<ProjectDataState>,
    setLoadedCsib: (itemId: string, csib: string | undefined) => void,
    setLoadedCenter: (itemId: string, center: Point2D | undefined) => void,
    setLoadedCrsIssue: (itemId: string, issue: CrsIssueKind | undefined) => void
  ) {
    const { selectedProject, selectedTenantTrn, selectedRegion } =
      useConnectProject();
    const {
      scanDataApi,
      alignmentApi,
      tcTcpsApi,
      crsResolver,
      coordinatesClient,
    } = useApiServices();
    const { formattingSettings, alignmentSettings } = useProjectSettings();

    // Match the metadata provider to the pane that is loading — using the
    // first provider would re-paint pane 0 when pane 1 loads a trajectory.
    const getTrajectoryMetadataProviderForViewer = (
      viewer: Web3DViewer
    ): TrajectoryMetadataProvider | undefined =>
      this.providers().find(
        (prov): prov is TrajectoryMetadataProvider =>
          prov instanceof TrajectoryMetadataProvider && prov.viewer === viewer
      );

    const getAllTrajectoryMetadataProviders =
      (): TrajectoryMetadataProvider[] =>
        this.providers().filter(
          (prov): prov is TrajectoryMetadataProvider =>
            prov instanceof TrajectoryMetadataProvider
        );

    const resolveSplittable = (
      id: string,
      signal?: AbortSignal
    ): Promise<TrajectoryItem | undefined> =>
      resolveTrajectorySplittable(projectDataState, id, signal);

    const trajectoryHandler = new ProviderHandler(
      // Trajectory geometry loads through TrimBimCapabilityProvider; API type is still `trajectory`.
      createTrimBimConfig(
        alignmentApi,
        selectedTenantTrn,
        formattingSettings,
        getTrajectoryMetadataProviderForViewer,
        getAllTrajectoryMetadataProviders,
        alignmentSettings,
        resolveSplittable
      ),
      projectDataState,
      setProjectDataState
    );

    const styledTrajectoryHandler = new ProviderHandler(
      createStyledTrajectoryConfig(
        formattingSettings,
        getTrajectoryMetadataProviderForViewer,
        getAllTrajectoryMetadataProviders,
        alignmentSettings,
        projectDataState
      ),
      projectDataState,
      setProjectDataState
    );

    const pointCloudLoadErrorNotifier = createPointCloudLoadErrorNotifier();

    const pointCloudConfig = createPointCloudConfig(
      scanDataApi,
      crsResolver,
      selectedRegion,
      selectedProject,
      setLoadedCsib,
      setLoadedCenter,
      setLoadedCrsIssue,
      () => pointCloudLoadErrorNotifier.report(),
      coordinatesClient
    );
    const pointCloudHandler = new ProviderHandler(
      pointCloudConfig,
      projectDataState,
      setProjectDataState
    );

    const modelHandler = new ProviderHandler(
      createModelConfig(
        tcTcpsApi,
        selectedRegion,
        getTrajectoryMetadataProviderForViewer,
        getAllTrajectoryMetadataProviders
      ),
      projectDataState,
      setProjectDataState
    );

    const dataLoadingServiceConfig: DataLoadingServiceConfig<
      ItemType,
      ProjectDataState
    > = {
      dataState: projectDataState,
      setDataState: setProjectDataState,
      handlers: [
        {
          type: 'trajectory',
          handler: trajectoryHandler,
          queueKey: 'trajectory',
          sliceKey: 'trajectory',
        },
        {
          type: 'pointCloud',
          handler: pointCloudHandler,
          queueKey: 'pointCloud',
          sliceKey: 'pointCloud',
        },
        {
          type: 'model',
          handler: modelHandler,
          queueKey: 'model',
          sliceKey: 'tcFileFolder',
        },
      ],
      transformHandlers: [
        {
          type: 'styled-trajectory',
          handler: styledTrajectoryHandler,
          queueKey: 'trajectory',
          sliceKey: 'trajectory',
        },
      ],
      queueConcurrency: {
        pointCloud: POINT_CLOUD_LOAD_CONCURRENCY,
      },
    };

    super(dataLoadingServiceConfig);

    this.cancelPointCloudQueries = (pointCloudId?: string) =>
      scanDataApi.cancelPointCloudQueries(pointCloudId);
    this.pointCloudLoadErrorNotifier = pointCloudLoadErrorNotifier;
    this.abortAllMismatchChecks = () =>
      pointCloudConfig.abortAllMismatchChecks();
  }

  public override unload(type: ItemType, itemId: string): void {
    if (type === 'pointCloud') {
      void this.cancelPointCloudQueries(itemId);
    }
    super.unload(type, itemId);
  }

  public cancelAllLoads(): void {
    this.cancelAllOperations();
    void this.cancelPointCloudQueries();
    this.pointCloudLoadErrorNotifier.cancel();
    this.abortAllMismatchChecks();
  }

  public cancelCrsMismatchChecks(): void {
    this.abortAllMismatchChecks();
  }
}
